import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { invoiceService } from '../services/firebase';
import { Invoice } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import { CreditCardIcon, ArrowDownTrayIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { downloadInvoicePdf, printInvoicePdf } from '../utils/invoicePdf';

const InvoiceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStatusSaving, setIsStatusSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const withPdf = async (action: (inv: Invoice) => Promise<void>) => {
    if (!invoice) return;
    setPdfBusy(true);
    try {
      await action(invoice);
    } catch (err) {
      console.error('Error generating invoice PDF:', err);
      toast.error('Could not generate the PDF. Please try again.');
    } finally {
      setPdfBusy(false);
    }
  };

  useEffect(() => {
    const fetchInvoice = async () => {
      if (!id) {
        setError('No invoice ID provided.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const fetchedInvoice = await invoiceService.getInvoice(id);
        if (fetchedInvoice) {
          setInvoice(fetchedInvoice);
        } else {
          setError('Invoice not found.');
        }
      } catch (err) {
        setError('Failed to fetch invoice.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [id]);

  const handleStatusChange = async (newStatus: string) => {
    if (!invoice) return;
    
    const newStatusTyped = newStatus as Invoice['status'];

    setIsStatusSaving(true);
    try {
      await invoiceService.updateInvoice(invoice.id, { status: newStatusTyped });
      setInvoice({ ...invoice, status: newStatusTyped });
    } catch (err) {
      console.error("Failed to update status:", err);
      // Optionally show an error toast to the user
    } finally {
      setIsStatusSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="text-center text-red-500 text-lg py-12">{error}</div>;
  }

  if (!invoice) {
    return <div className="text-center text-gray-500 text-lg py-12">No invoice data available.</div>;
  }
  
  const { 
    invoiceNumber, status, customer, dueDate, createdAt, 
    items, subtotal, tax, total, notes 
  } = invoice;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Invoice {invoiceNumber}</h1>
            <p className="text-gray-500">
              Created on: {new Date(createdAt).toLocaleDateString()}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => withPdf(downloadInvoicePdf)}
                disabled={pdfBusy}
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                {pdfBusy ? 'Preparing…' : 'Download PDF'}
              </button>
              <button
                onClick={() => withPdf(printInvoicePdf)}
                disabled={pdfBusy}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3.5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                <PrinterIcon className="h-4 w-4" />
                Print
              </button>
            </div>
          </div>
          <div className="text-right">
             <div className="relative inline-block text-left">
              <select
                value={status}
                onChange={(e) => handleStatusChange(e.target.value)}
                disabled={isStatusSaving}
                className={`appearance-none w-full bg-transparent font-semibold py-1 pl-3 pr-8 border rounded-full transition capitalize ${
                  isStatusSaving ? 'cursor-not-allowed bg-gray-200' : 'hover:border-gray-400'
                } ${
                  status === 'paid' ? 'border-green-300 bg-green-100 text-green-800' :
                  status === 'pending' ? 'border-orange-300 bg-orange-100 text-orange-800' :
                  status === 'overdue' ? 'border-red-300 bg-red-100 text-red-800' : 'border-yellow-300 bg-yellow-100 text-yellow-800'
                }`}
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Due Date: {new Date(dueDate).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Customer Info & Payment Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
                <h2 className="text-lg font-semibold text-gray-700 mb-2">Billed To:</h2>
                {customer ? (
                    <Link to={`/customers/${customer.id}`} className="text-blue-600 hover:underline">
                    <p className="font-bold">{customer.name}</p>
                    <p>{customer.email}</p>
                    </Link>
                ) : (
                    <p className="italic text-gray-500">No customer assigned.</p>
                )}
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
                <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                    <CreditCardIcon className="h-6 w-6 mr-2 text-gray-500" />
                    Payment Information
                </h2>
                
                {invoice.paymentData ? (
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Payment Method:</span>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                invoice.paymentData.paymentMethod === 'cash' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-blue-100 text-blue-800'
                            }`}>
                                {invoice.paymentData.paymentMethod === 'cash' ? 'Cash Payment' : 'Bank Transfer'}
                            </span>
                        </div>
                        
                        {invoice.paymentData.uploadedAt && (
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700">Payment Date:</span>
                                <span className="text-sm text-gray-600">
                                    {new Date(invoice.paymentData.uploadedAt).toLocaleDateString()}
                                </span>
                            </div>
                        )}
                        
                        {invoice.paymentData.receiptImageUrl && (
                            <div className="mt-4">
                                <p className="text-sm font-medium text-gray-700 mb-2">Payment Receipt:</p>
                                <a 
                                    href={invoice.paymentData.receiptImageUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="inline-block"
                                >
                                    <img 
                                        src={invoice.paymentData.receiptImageUrl} 
                                        alt="Payment Receipt" 
                                        className="w-32 h-32 object-cover rounded-lg border border-gray-200 hover:opacity-80 transition-opacity"
                                    />
                                </a>
                                <p className="text-xs text-gray-500 mt-1">Click to view full size</p>
                            </div>
                        )}
                        
                        {status === 'pending' && (
                            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <p className="text-sm text-yellow-800">
                                    ⏳ Payment pending confirmation. Please verify the payment and update the status.
                                </p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-center">
                        <p className="text-gray-600 text-sm mb-4">
                            No payment information available.
                        </p>
                        <div className="text-xs text-gray-500">
                            Payment status: {status}
                        </div>
                    </div>
                )}
            </div>
        </div>

        {/* Items Table */}
        <div className="mb-8">
          <table className="w-full text-left">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-3 font-semibold">Description</th>
                <th className="p-3 font-semibold text-right">Quantity</th>
                <th className="p-3 font-semibold text-right">Unit Price</th>
                <th className="p-3 font-semibold text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={index} className="border-b">
                  <td className="p-3">{item.description}</td>
                  <td className="p-3 text-right">{item.quantity}</td>
                  <td className="p-3 text-right">${item.unitPrice.toFixed(2)}</td>
                  <td className="p-3 text-right">${item.total.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals Section */}
        <div className="flex justify-end mb-8">
          <div className="w-full max-w-xs space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Subtotal:</span>
              <span className="font-semibold">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Tax ({tax}%):</span>
              <span className="font-semibold">${(subtotal * tax / 100).toFixed(2)}</span>
            </div>
            <hr />
            <div className="flex justify-between text-xl font-bold">
              <span>Total:</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {notes && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-700 mb-2">Notes:</h3>
            <p className="text-gray-600">{notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceDetail; 